import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    CircularProgress,
} from '@mui/material';
import { QRCodeSVG } from 'qrcode.react';
import { QrCode as QrCodeIcon, Close as CloseIcon } from '@mui/icons-material';

/**
 * OTP QR Code Modal Component
 * Displays a QR code containing the OTP for user login authentication
 */
function OtpQrModal({
    open,
    onClose,
    otpData,
    username,
    loading,
    error,
}) {
    const { t } = useTranslation();
    const [timeRemaining, setTimeRemaining] = useState(null);

    // Calculate and update remaining time
    useEffect(() => {
        if (!otpData?.expiryTime) {
            setTimeRemaining(null);
            return;
        }

        const calculateTimeRemaining = () => {
            const expiryTime = new Date(otpData.expiryTime).getTime();
            const now = Date.now();
            const remaining = Math.max(0, Math.floor((expiryTime - now) / 1000));
            return remaining;
        };

        setTimeRemaining(calculateTimeRemaining());

        const interval = setInterval(() => {
            const remaining = calculateTimeRemaining();
            setTimeRemaining(remaining);
            if (remaining <= 0) {
                clearInterval(interval);
            }
        }, 1000);

        return () => clearInterval(interval);
    }, [otpData?.expiryTime]);

    // Format time as mm:ss
    const formatTime = (seconds) => {
        if (seconds === null || seconds === undefined) return '--:--';
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    const isExpired = timeRemaining !== null && timeRemaining <= 0;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: { borderRadius: 2 }
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    borderBottom: '1px solid #e5e7eb',
                    pb: 2,
                }}
            >
                <QrCodeIcon color="primary" />
                {t('accounts.otpLogin.title')}
            </DialogTitle>
            <DialogContent sx={{ py: 3 }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
                        <Typography color="error">{error}</Typography>
                    </Box>
                ) : otpData ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                        {/* QR Code */}
                        <Box
                            sx={{
                                p: 3,
                                backgroundColor: '#ffffff',
                                borderRadius: 2,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                opacity: isExpired ? 0.5 : 1,
                            }}
                        >
                            <QRCodeSVG
                                value={otpData.otp}
                                size={200}
                                level="H"
                                includeMargin={true}
                            />
                        </Box>

                        {/* Time remaining indicator */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                px: 2,
                                py: 1,
                                borderRadius: 1,
                                backgroundColor: isExpired ? '#fee2e2' : '#f0f9ff',
                            }}
                        >
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 500,
                                    color: isExpired ? '#dc2626' : '#0369a1',
                                }}
                            >
                                {isExpired
                                    ? t('accounts.otpLogin.expired')
                                    : `${t('accounts.otpLogin.timeRemaining')}: ${formatTime(timeRemaining)}`
                                }
                            </Typography>
                        </Box>

                        {/* Description */}
                        <Typography
                            variant="body1"
                            sx={{
                                textAlign: 'center',
                                color: 'text.secondary',
                                maxWidth: 400,
                                lineHeight: 1.6,
                            }}
                        >
                            {t('accounts.otpLogin.description', {
                                username: username,
                                expiryMinutes: otpData.expiryMinutes,
                            })}
                        </Typography>
                    </Box>
                ) : null}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, borderTop: '1px solid #e5e7eb', pt: 2 }}>
                <Button
                    onClick={onClose}
                    variant="contained"
                    startIcon={<CloseIcon />}
                    id="closeOtpModal"
                    name="closeOtpModal"
                    data-testid="accounts-button-closeOtpModal"
                >
                    {t('accounts.otpLogin.close')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

OtpQrModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    otpData: PropTypes.shape({
        otp: PropTypes.string,
        expiryMinutes: PropTypes.number,
        expiryTime: PropTypes.string,
    }),
    username: PropTypes.string,
    loading: PropTypes.bool,
    error: PropTypes.string,
};

OtpQrModal.defaultProps = {
    otpData: null,
    username: '',
    loading: false,
    error: null,
};

export default OtpQrModal;
